import type { ClaimMarkerScheme } from "@patent-studio/api-types";
import { bijectiveBase26 } from "@/lib/bijective-base-26";
import type { TextBlock } from "@/lib/structured-text";

// As far as the server's own table and no further: its docstring rests on the two
// reaching the same distance, and a claim holds forty lines, so neither is asked
// past the tens in the first place.
const ROMAN: ReadonlyArray<readonly [number, string]> = [
  [100, "c"],
  [90, "xc"],
  [50, "l"],
  [40, "xl"],
  [10, "x"],
  [9, "ix"],
  [5, "v"],
  [4, "iv"],
  [1, "i"],
];

/** i, ii, iii: the sub-element's own numbering, in the case a claim is set in. */
function roman(n: number): string {
  let left = n;
  let spelled = "";
  for (const [value, sign] of ROMAN)
    while (left >= value) {
      spelled += sign;
      left -= value;
    }
  return spelled;
}

/**
 * How a matter spells the markers on its claim lines, elements first and then
 * the sub-elements under them.
 *
 * Taken from the served schema rather than written again here, so a scheme the
 * server learns to spell is a type error until this projection spells it too.
 */
export type { ClaimMarkerScheme };

/** The default, and what every matter drafted before the choice existed reads in. */
export const DEFAULT_MARKER_SCHEME: ClaimMarkerScheme = "a-i";

const SPELLINGS: Record<
  ClaimMarkerScheme,
  readonly [(n: number) => string, (n: number) => string]
> = {
  "a-i": [bijectiveBase26, roman],
  "i-a": [roman, bijectiveBase26],
};

/**
 * Each line's marker, spelled as the matter's scheme draws it, and none for a
 * preamble.
 *
 * The same rule the server projects when it serves a claim, written again here
 * because the editor draws a marker while the attorney is still writing, before
 * any answer has come back. A claim whose letters move when it is saved is a
 * claim the attorney cannot trust, so both are held to
 * `packages/contracts/fixtures/claim-line-markers.json`.
 */
export function claimMarkers(
  blocks: readonly TextBlock[],
  scheme: ClaimMarkerScheme = DEFAULT_MARKER_SCHEME,
): (string | null)[] {
  const [spellElement, spellSub] = SPELLINGS[scheme];
  const markers: (string | null)[] = [];
  let elements = 0;
  let subElements = 0;
  for (const block of blocks) {
    if (block.depth === 0) elements = 0;
    if (block.depth <= 1) subElements = 0;
    if (!block.numbered || block.depth === 0) {
      markers.push(null);
      continue;
    }
    if (block.depth === 1) {
      elements += 1;
      markers.push(`${spellElement(elements)})`);
    } else {
      subElements += 1;
      markers.push(`${spellSub(subElements)})`);
    }
  }
  return markers;
}
